import { useEffect, useState } from "react";
import { Loader2, Save, LogOut, LayoutDashboard, Wrench, Map, BookOpen, HelpCircle, Tag, Settings, Car, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AdminLogin } from "./AdminLogin";
import CrudEditor, { Field } from "./CrudEditor";
import api from "@/lib/api";

const TABS = [
  { id: "dashboard", label: "الرئيسية", icon: LayoutDashboard },
  { id: "services", label: "الخدمات", icon: Wrench },
  { id: "brands", label: "العلامات التجارية", icon: Tag },
  { id: "neighborhoods", label: "الأحياء والتغطية", icon: Map },
  { id: "articles", label: "المقالات", icon: BookOpen },
  { id: "faqs", label: "الأسئلة الشائعة", icon: HelpCircle },
  { id: "categories", label: "التصنيفات", icon: Car },
  { id: "settings", label: "الإعدادات", icon: Settings },
];

const ZONES = ["north", "northeast", "northwest", "east", "west", "central", "south", "southeast", "southwest"];
const STATUS_OPTS = [
  { value: "available", label: "التغطية متاحة" },
  { value: "after_confirmation", label: "متاحة بعد التأكيد" },
  { value: "unavailable", label: "غير متاحة حاليًا" },
];

export default function AdminPage() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ink"><Loader2 className="animate-spin text-gold" size={34} /></div>;
  if (!user) return <AdminLogin />;

  return (
    <div dir="rtl" className="min-h-screen bg-ink pt-6" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-3xl border border-white/10 bg-coal/60 p-6">
          <div>
            <p className="font-display text-xs font-bold uppercase tracking-[0.3em] text-gold">MERO ADMIN</p>
            <h1 className="mt-1 text-xl font-bold text-white">لوحة تحكم ميرو</h1>
            <p className="mt-1 text-xs text-dim">{user.email}</p>
          </div>
          <div className="flex gap-3">
            <a href="/" target="_blank" rel="noopener" className="btn-ghost !px-5 !py-2.5 text-sm"><Globe size={15} /> الموقع</a>
            <button onClick={logout} className="btn-ghost !px-5 !py-2.5 text-sm !text-[#f87171] !border-[#EF4444]/40" data-testid="admin-logout-button"><LogOut size={15} /> خروج</button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
          <aside className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex shrink-0 items-center gap-2.5 rounded-2xl px-5 py-3 text-sm font-bold transition-all ${tab === id ? "bg-gold text-ink" : "border border-white/10 bg-coal/50 text-zinc-300 hover:border-gold/40"}`}
                data-testid={`admin-tab-${id}`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </aside>

          <main className="rounded-3xl border border-white/10 bg-coal/30 p-6 sm:p-8">
            {tab === "dashboard" && <DashboardSummary />}
            {tab === "services" && (
              <CrudEditor name="services" title="الخدمات" displayField="title_ar" fields={[
                { key: "title_ar", label: "العنوان (عربي)" }, { key: "title_en", label: "العنوان (إنجليزي)", ltr: true },
                { key: "slug", label: "الرابط (slug)", ltr: true }, { key: "order", label: "الترتيب", type: "number", default: 99 },
                { key: "short_ar", label: "الوصف المختصر (عربي)", type: "textarea", wide: true }, { key: "short_en", label: "الوصف المختصر (إنجليزي)", type: "textarea", wide: true, ltr: true },
                { key: "desc_ar", label: "الوصف الكامل (عربي)", type: "textarea", big: true, wide: true }, { key: "desc_en", label: "الوصف الكامل (إنجليزي)", type: "textarea", big: true, wide: true, ltr: true },
                { key: "cases_ar", label: "حالات الحاجة (عربي)", type: "list" }, { key: "cases_en", label: "حالات الحاجة (إنجليزي)", type: "list" },
                { key: "image", label: "الصورة", type: "image" },
                { key: "wa_template_ar", label: "رسالة واتساب (عربي)", type: "textarea", wide: true },
                { key: "wa_template_en", label: "رسالة واتساب (إنجليزي)", type: "textarea", wide: true, ltr: true },
                { key: "has_page", label: "لديها صفحة مستقلة؟", type: "select", default: false, options: [{ value: true, label: "نعم" }, { value: false, label: "لا" }] },
                { key: "published", label: "منشورة؟", type: "select", default: true, options: [{ value: true, label: "نعم" }, { value: false, label: "لا" }] },
              ]} />
            )}
            {tab === "brands" && (
              <CrudEditor name="brands" title="العلامات التجارية" displayField="name_ar" fields={[
                { key: "name_ar", label: "الاسم (عربي)" }, { key: "name_en", label: "الاسم (إنجليزي)", ltr: true },
                { key: "slug", label: "الرابط", ltr: true }, { key: "order", label: "الترتيب", type: "number", default: 99 },
                { key: "origin_ar", label: "بلد المنشأ (عربي)" }, { key: "origin_en", label: "بلد المنشأ (إنجليزي)", ltr: true },
                { key: "tag_ar", label: "وسم (عربي)" }, { key: "tag_en", label: "وسم (إنجليزي)", ltr: true },
                { key: "blurb_ar", label: "نبذة (عربي)", type: "textarea", wide: true }, { key: "blurb_en", label: "نبذة (إنجليزي)", type: "textarea", wide: true, ltr: true },
                { key: "published", label: "منشورة؟", type: "select", default: true, options: [{ value: true, label: "نعم" }, { value: false, label: "لا" }] },
              ]} />
            )}
            {tab === "neighborhoods" && (
              <CrudEditor name="neighborhoods" title="الأحياء وحالة التغطية" displayField="name_ar" fields={[
                { key: "name_ar", label: "اسم الحي (عربي)" }, { key: "name_en", label: "اسم الحي (إنجليزي)", ltr: true },
                { key: "slug", label: "الرابط", ltr: true },
                { key: "zone", label: "المنطقة", type: "select", default: "north", options: ZONES.map((z) => ({ value: z, label: z })) },
                { key: "status", label: "حالة التغطية", type: "select", default: "after_confirmation", options: STATUS_OPTS },
                { key: "order", label: "الترتيب", type: "number", default: 99 },
              ]} />
            )}
            {tab === "articles" && (
              <CrudEditor name="articles" title="المقالات" displayField="title" previewBase="/blog" fields={[
                { key: "title", label: "العنوان", wide: true }, { key: "en_title", label: "العنوان (إنجليزي)", wide: true, ltr: true },
                { key: "slug", label: "الرابط (slug)", ltr: true },
                { key: "category", label: "التصنيف", type: "select", default: "tire-tips", options: ["tire-tips", "mobile-pitstop", "tpms", "tire-sizes", "maintenance", "batteries", "road-safety", "drivers-guide"].map((c) => ({ value: c, label: c })) },
                { key: "excerpt", label: "المقدمة/الملخص", type: "textarea", wide: true },
                { key: "content", label: "المحتوى (HTML مسموح: h2, h3, p, ul, li, strong)", type: "textarea", big: true, wide: true },
                { key: "image", label: "الصورة الرئيسية", type: "image" },
                { key: "tags", label: "الوسوم", type: "list" },
                { key: "read_time", label: "وقت القراءة (دقائق)", type: "number", default: 5 },
                { key: "status", label: "الحالة", type: "select", default: "published", options: [{ value: "published", label: "منشور" }, { value: "draft", label: "مسودة" }] },
                { key: "seo_title", label: "عنوان SEO", wide: true }, { key: "seo_desc", label: "وصف SEO", type: "textarea", wide: true },
                { key: "created_at", label: "تاريخ النشر (ISO)", ltr: true },
              ]} />
            )}
            {tab === "faqs" && (
              <CrudEditor name="faqs" title="الأسئلة الشائعة" displayField="q_ar" fields={[
                { key: "q_ar", label: "السؤال (عربي)", wide: true }, { key: "q_en", label: "السؤال (إنجليزي)", wide: true, ltr: true },
                { key: "a_ar", label: "الجواب (عربي)", type: "textarea", wide: true }, { key: "a_en", label: "الجواب (إنجليزي)", type: "textarea", wide: true, ltr: true },
                { key: "order", label: "الترتيب", type: "number", default: 99 },
              ]} />
            )}
            {tab === "categories" && (
              <CrudEditor name="categories" title="التصنيفات" displayField="name_ar" fields={[
                { key: "id", label: "المعرف (id)", ltr: true }, { key: "name_ar", label: "الاسم (عربي)" },
                { key: "name_en", label: "الاسم (إنجليزي)", ltr: true }, { key: "order", label: "الترتيب", type: "number", default: 99 },
              ]} />
            )}
            {tab === "settings" && <SettingsEditor />}
          </main>
        </div>
      </div>
    </div>
  );
}

function DashboardSummary() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/summary").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gold" /></div>;
  const cards = [["الخدمات", s.services], ["العلامات", s.brands], ["الأحياء", s.neighborhoods], ["المقالات", s.articles], ["الأسئلة", s.faqs], ["التصنيفات", s.categories]];
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="admin-summary">
      {cards.map(([label, n]) => (
        <div key={label} className="rounded-2xl border border-white/10 bg-ink/70 p-6 text-center">
          <p className="font-display text-4xl font-extrabold text-gold">{n}</p>
          <p className="mt-2 text-sm text-mist">{label}</p>
        </div>
      ))}
    </div>
  );
}

function SettingsEditor() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    api.get("/admin/list/settings").then(({ data }) => setS(data?.[0] || {})).catch(() => {});
  }, []);

  if (!s) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-gold" /></div>;

  const set = (k) => (e) => setS((v) => ({ ...v, [k]: e.target.value }));
  const setHero = (k) => (e) => setS((v) => ({ ...v, hero: { ...v.hero, [k]: e.target.value } }));

  const save = async () => {
    setBusy(true);
    await api.put("/admin/settings", s).catch(() => alert("فشل الحفظ"));
    setBusy(false);
    setOk(true);
    setTimeout(() => setOk(false), 2500);
  };

  return (
    <div data-testid="admin-settings-editor">
      <h2 className="mb-6 text-xl font-bold text-white">الإعدادات ومعلومات التواصل</h2>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="رقم الهاتف (للعرض)"><input className="field" dir="ltr" value={s.phone_display || ""} onChange={set("phone_display")} /></Field>
        <Field label="رابط الاتصال"><input className="field" dir="ltr" value={s.phone_tel || ""} onChange={set("phone_tel")} /></Field>
        <Field label="رابط واتساب"><input className="field" dir="ltr" value={s.whatsapp_url || ""} onChange={set("whatsapp_url")} /></Field>
        <Field label="رقم واتساب (دولي)"><input className="field" dir="ltr" value={s.whatsapp_number || ""} onChange={set("whatsapp_number")} /></Field>
        <Field label="اسم العلامة (عربي)"><input className="field" value={s.brand_ar || ""} onChange={set("brand_ar")} /></Field>
        <Field label="اسم العلامة (إنجليزي)"><input className="field" dir="ltr" value={s.brand_en || ""} onChange={set("brand_en")} /></Field>

        <Field label="عنوان الهيرو — سطر 1 (عربي)"><input className="field" value={(s.hero?.title_ar || [])[0] || ""} onChange={(e) => setS((v) => ({ ...v, hero: { ...v.hero, title_ar: [e.target.value, (v.hero?.title_ar || [])[1] || ""] } }))} /></Field>
        <Field label="عنوان الهيرو — سطر 2 (عربي)"><input className="field" value={(s.hero?.title_ar || [])[1] || ""} onChange={(e) => setS((v) => ({ ...v, hero: { ...v.hero, title_ar: [(v.hero?.title_ar || [])[0] || "", e.target.value] } }))} /></Field>
        <Field label="Hero line 1 (EN)"><input className="field" dir="ltr" value={(s.hero?.title_en || [])[0] || ""} onChange={(e) => setS((v) => ({ ...v, hero: { ...v.hero, title_en: [e.target.value, (v.hero?.title_en || [])[1] || ""] } }))} /></Field>
        <Field label="Hero line 2 (EN)"><input className="field" dir="ltr" value={(s.hero?.title_en || [])[1] || ""} onChange={(e) => setS((v) => ({ ...v, hero: { ...v.hero, title_en: [(v.hero?.title_en || [])[0] || "", e.target.value] } }))} /></Field>

        <Field label="الوصف (عربي)" wide><textarea className="field min-h-[80px]" value={s.hero?.subtitle_ar || ""} onChange={setHero("subtitle_ar")} /></Field>
        <Field label="الوصف (إنجليزي)" wide><textarea className="field min-h-[80px]" dir="ltr" value={s.hero?.subtitle_en || ""} onChange={setHero("subtitle_en")} /></Field>

        <Field label="عنوان SEO (عربي)" wide><input className="field" value={s.seo?.title_ar || ""} onChange={(e) => setS((v) => ({ ...v, seo: { ...v.seo, title_ar: e.target.value } }))} /></Field>
        <Field label="وصف SEO (عربي)" wide><textarea className="field min-h-[80px]" value={s.seo?.desc_ar || ""} onChange={(e) => setS((v) => ({ ...v, seo: { ...v.seo, desc_ar: e.target.value } }))} /></Field>
        <Field label="SEO title (EN)" wide><input className="field" dir="ltr" value={s.seo?.title_en || ""} onChange={(e) => setS((v) => ({ ...v, seo: { ...v.seo, title_en: e.target.value } }))} /></Field>
        <Field label="SEO desc (EN)" wide><textarea className="field min-h-[80px]" dir="ltr" value={s.seo?.desc_en || ""} onChange={(e) => setS((v) => ({ ...v, seo: { ...v.seo, desc_en: e.target.value } }))} /></Field>
      </div>

      <button onClick={save} disabled={busy} className="btn-gold mt-6 !px-7 !py-3" data-testid="admin-settings-save">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {ok ? "تم الحفظ ✓" : "حفظ الإعدادات"}
      </button>
    </div>
  );
}
